import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronDown, ChevronRight, Code, Copy, ExternalLink, Plus, Save, Settings, Trash2, Workflow } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import { RequestResponseDTO } from "../../../models/workFlow/ConnectionModel";
import {
  Claim,
  ClaimDeleteConfirmationData,
  DeleteConfirmationData,
  FlowStep,
  ParameterMapping,
} from "../../../models/workFlow/ExecutionFlowBuilder";
import {
  ExecutionFlowModel,
  RequestType,
} from "../../../models/workFlow/ExecutionFlowModel";
import {
  fetchConnectionById,
  fetchRequestResponse,
  fetchSwaggerEndPoint,
} from "../../../services/AdminServices/ConnectionService";
import {
  GetExecutionByConnectionId,
  updateExecutionFlow,
} from "../../../services/AdminServices/ExecutionFlowService";
import ClaimCard from "./ClaimCard";
import StepCard from "./StepCard";

const ExecutionFlowBuilder: React.FC = () => {
  const { connectionId } = useParams<{ connectionId: string }>();

  const location = useLocation();

  // Destructure the connection object from state
  const connection = location.state?.connection;

  const { accessToken, userData } = useAppContext();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([]);
  const [swaggerEndpoints, setSwaggerEndpoints] = useState<RequestType[]>([]);
  const [flowName, setFlowName] = useState<string>("");
  const [flowDescription, setFlowDescription] = useState<string>("");
  const [showJsonPreview, setShowJsonPreview] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [executionFlowId, setExecutionFlowId] = useState<number | null>(null);

  // Add these state variables after your existing useState declarations:
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [selectedStep, setSelectedStep] = useState<FlowStep | null>(null);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [stepDrawerOpen, setStepDrawerOpen] = useState(false);
  const [isSwaggerEnabled, setIsSwaggerEnabled] = useState<boolean>(false);
  const [swaggerUrl, setSwaggerUrl] = useState<string>("");



  // Delete confirmation dialog states
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const [deleteConfirmationData, setDeleteConfirmationData] =
    useState<DeleteConfirmationData | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // Add these state variables after your existing useState declarations:
  const [claimDeleteDialogOpen, setClaimDeleteDialogOpen] = useState(false);
  const [claimDeleteConfirmationData, setClaimDeleteConfirmationData] =
    useState<ClaimDeleteConfirmationData | null>(null);

  // Add these synchronized update functions:
  const updateSelectedClaim = (
    claimId: number,
    field: keyof Claim,
    value: any
  ) => {
    // Update the main claims array
    updateClaim(claimId, field, value);

    // Also update the selectedClaim to keep the drawer in sync
    if (selectedClaim && selectedClaim.id === claimId) {
      setSelectedClaim((prev) => {
        if (!prev) return null;
        return { ...prev, [field]: value };
      });
    }
  };

  const updateSelectedStep = (
    stepId: number,
    field: keyof FlowStep,
    value: any
  ) => {
    // Update the main steps array
    updateStep(stepId, field, value);

    // Also update the selectedStep to keep the drawer in sync
    if (selectedStep && selectedStep.id === stepId) {
      setSelectedStep((prev) => {
        if (!prev) return null;
        return { ...prev, [field]: value };
      });
    }
  };

  const updateSelectedStepParameter = (
    stepId: number,
    paramName: string,
    paramValue: string
  ) => {
    // Update the main steps array

    updateParameter(stepId, paramName, paramValue);

    // Also update the selectedStep to keep the drawer in sync
    if (selectedStep && selectedStep.id === stepId) {
      setSelectedStep((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          parameters: {
            ...prev.parameters,
            [paramName]: paramValue,
          },
        };
      });
    }
  };

  const updateSelectedStepParameterMapping = (
    stepId: number,
    mapping: ParameterMapping
  ) => {
    // Update the main steps array
    updateParameterMapping(stepId, mapping);

    // Also update the selectedStep to keep the drawer in sync
    if (selectedStep && selectedStep.id === stepId) {
      setSelectedStep((prev) => {
        if (!prev) return null;
        const updatedMappings = prev.parameterMappings.map((m) =>
          m.id === mapping.id ? mapping : m
        );
        return { ...prev, parameterMappings: updatedMappings };
      });
    }
  };

  const addSelectedStepParameterMapping = (stepId: number) => {
    // Update the main steps array
    addParameterMapping(stepId);

    // Also update the selectedStep to keep the drawer in sync
    if (selectedStep && selectedStep.id === stepId) {
      const newMapping: ParameterMapping = {
        id: Date.now(),
        source: "",
        target: "",
        sourceType: "claim",
      };
      setSelectedStep((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          parameterMappings: [...prev.parameterMappings, newMapping],
        };
      });
    }
  };

  const removeSelectedStepParameterMapping = (
    stepId: number,
    mappingId: number
  ) => {
    // Update the main steps array
    removeParameterMapping(stepId, mappingId);

    // Also update the selectedStep to keep the drawer in sync
    if (selectedStep && selectedStep.id === stepId) {
      setSelectedStep((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          parameterMappings: prev.parameterMappings.filter(
            (m) => m.id !== mappingId
          ),
        };
      });
    }
  };

  // Function to find steps that reference a specific claim
  const findStepsReferencingClaim = (
    claimId: number
  ): Array<{ id: number; name: string; index: number }> => {
    const affectedSteps: Array<{ id: number; name: string; index: number }> =
      [];

    flowSteps.forEach((step, index) => {
      // Check if any parameter mappings reference this claim
      const hasClaimReference = step.parameterMappings.some(
        (mapping) =>
          mapping.sourceType === "claim" &&
          mapping.source.includes(claimId.toString())
      );

      if (hasClaimReference) {
        affectedSteps.push({
          id: step.id,
          name: step.apiEndpoint || `Step ${index + 1}`,
          index: index + 1,
        });
      }
    });

    return affectedSteps;
  };

  // Function to handle claim deletion with confirmation
  const handleClaimDeleteClick = (
    claimId: number,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
    }

    const claim = claims.find((c) => c.id === claimId);
    if (!claim) return;

    const affectedSteps = findStepsReferencingClaim(claimId);

    setClaimDeleteConfirmationData({
      claimId,
      claimName: claim.name || "Untitled Claim",
      affectedSteps,
    });

    setClaimDeleteDialogOpen(true);
  };

  // Function to confirm and execute claim deletion
  const confirmClaimDeletion = () => {
    if (!claimDeleteConfirmationData) return;

    const { claimId, affectedSteps } = claimDeleteConfirmationData;

    // Remove the claim
    setClaims(claims.filter((claim) => claim.id !== claimId));

    // Update affected steps to remove references to this claim
    if (affectedSteps.length > 0) {
      setFlowSteps(
        flowSteps.map((step) => {
          const isAffected = affectedSteps.some(
            (affected) => affected.id === step.id
          );
          if (isAffected) {
            // Remove parameter mappings that reference the deleted claim
            console.log("Removing claim references from step:", step);
            const updatedMappings = step.parameterMappings.filter(
              (mapping) =>
                !(
                  mapping.sourceType === "claim" &&
                  mapping.source.includes(claimId.toString())
                )
            );
            console.log(
              "Updated parameter mappings for updatedMappings:",
              updatedMappings
            );
            return { ...step, parameterMappings: updatedMappings };
          }
          return step;
        })
      );
    }

    // Close drawer if the deleted claim was selected
    if (selectedClaim && selectedClaim.id === claimId) {
      handleCloseClaimDrawer();
    }

    setClaimDeleteDialogOpen(false);
    setClaimDeleteConfirmationData(null);

    toast({
      title: "Claim Deleted",
      description: `Claim deleted${affectedSteps.length > 0
        ? ` and removed from ${affectedSteps.length} step(s)`
        : ""
        }`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Function to find steps that reference the selected step
  const findDependentSteps = (
    stepIndex: number
  ): Array<{ id: number; index: number; name: string }> => {
    // We no longer consider subsequent steps as dependent
    // This function now returns an empty array since we're only deleting the selected step
    return [];
  };

  // Function to handle step deletion with confirmation
  const handleStepDeleteClick = (stepId: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    const stepIndex = flowSteps.findIndex((step) => step.id === stepId);
    if (stepIndex === -1) return;

    const step = flowSteps[stepIndex];
    const dependentSteps = findDependentSteps(stepIndex);

    setDeleteConfirmationData({
      stepId,
      stepIndex,
      stepName: step.apiEndpoint || `Step ${stepIndex + 1}`,
      dependentSteps,
    });

    onDeleteDialogOpen();
  };

  // Function to confirm and execute step deletion
  const confirmStepDeletion = () => {
    if (!deleteConfirmationData) return;

    const { stepId, stepIndex } = deleteConfirmationData;

    // Remove the selected step
    const updatedSteps = flowSteps.filter((step, index) => index !== stepIndex);

    // ✅ REORDER the remaining steps to fix order conflicts
    const reorderedSteps = updatedSteps
      .sort((a, b) => a.order - b.order)
      .map((step, index) => ({ ...step, order: index + 1 }));

    setFlowSteps(reorderedSteps);

    // Close drawer if the deleted step was selected
    if (selectedStep && selectedStep.id === stepId) {
      handleCloseStepDrawer();
    }

    onDeleteDialogClose();
    setDeleteConfirmationData(null);

    toast({
      title: "Step Deleted",
      description: `Deleted flow step successfully`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };
  // Add these click handlers:
  const handleClaimClick = (claim: Claim) => {
    setSelectedClaim(claim);
    setClaimDrawerOpen(true);
  };

  const handleStepClick = (step: FlowStep) => {
    debugStepMappings(step); // Add this line
    setSelectedStep(step);
    setStepDrawerOpen(true);
  };

  const handleCloseClaimDrawer = () => {
    setClaimDrawerOpen(false);
    setSelectedClaim(null);
  };

  const handleCloseStepDrawer = () => {
    setStepDrawerOpen(false);
    setSelectedStep(null);
  };

  // Add these useEffect hooks to keep drawers in sync:
  useEffect(() => {
    if (selectedClaim && claimDrawerOpen) {
      const updatedClaim = claims.find((c) => c.id === selectedClaim.id);
      if (updatedClaim) {
        setSelectedClaim(updatedClaim);
      }
    }
  }, [claims, selectedClaim?.id, claimDrawerOpen]);

  useEffect(() => {
    if (selectedStep && stepDrawerOpen) {
      const updatedStep = flowSteps.find((s) => s.id === selectedStep.id);
      if (updatedStep) {
        setSelectedStep(updatedStep);
      }
    }
  }, [flowSteps, selectedStep?.id, stepDrawerOpen]);

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (!accessToken || !connectionId) return;

      setLoading(true);
      try {

        const connectionDetail = await fetchConnectionById(
          accessToken,
          parseInt(connectionId)
        );
        const isSwaggerEnabled =
          connectionDetail?.[0]?.isSwaggerEnabled || false;
        const swaggerUrl = connectionDetail?.[0]?.swaggerUrl || "";
        setIsSwaggerEnabled(isSwaggerEnabled);
        setSwaggerUrl(swaggerUrl);
        setSwaggerEndpoints(
          isSwaggerEnabled
            ? await fetchSwaggerEndPoint(accessToken, parseInt(connectionId))
            : []
        );

        // Pass `isSwaggerEnabled` and `swaggerUrl` to claims and steps
        setClaims((prevClaims) =>
          prevClaims.map((claim) => ({
            ...claim,
            isSwaggerEnabled,
            swaggerUrl,
          }))
        );
        setFlowSteps((prevSteps) =>
          prevSteps.map((step) => ({
            ...step,
            isSwaggerEnabled,
            swaggerUrl,
          }))
        );

        // Load existing execution flow
        const existingFlow = await GetExecutionByConnectionId(
          accessToken,
          parseInt(connectionId)
        );
        console.log("Exsting flow : ", existingFlow);
        console.log("Exsting flow json : ", existingFlow?.workflowJson);
        if (existingFlow?.workflowJson) {
          setIsEditing(true);

          console.log("Inside parce Json");

          try {
            const workflowData = JSON.parse(existingFlow.workflowJson);

            // Set basic flow info
            setFlowName(workflowData.name || "");
            setFlowDescription(workflowData.description || "");

            // Set claims if they exist
            if (Array.isArray(workflowData.claims)) {

              const mappedClaims = workflowData.claims.map((claim: any) => ({
                id: claim.id || Date.now(),
                name: claim.name || "",
                apiEndpoint: claim.apiEndpoint || "",
                parameterSource: claim.parameterSource || "claim",
                parameterKey: claim.parameterKey || "",
                parameterValue: claim.parameterValue || "",
                requestGroup: claim.requestGroup || "",
                parameterMappings: Array.isArray(claim.parameterMappings)
                  ? claim.parameterMappings.map((m: any) => ({
                    id: m.id || Date.now(),
                    source: m.source || "",
                    target: m.target || "",
                    sourceType: m.sourceType || "claim",
                    sourceStepId: m.sourceStepId || undefined,
                  }))
                  : [],
                inputJson:
                  typeof claim.inputJson === "string"
                    ? claim.inputJson
                    : JSON.stringify(claim.inputJson || {}, null, 2),
                outputJson:
                  typeof claim.outputJson === "string"
                    ? claim.outputJson
                    : JSON.stringify(claim.outputJson || {}, null, 2),
              }));
              console.log("mappedClaims", mappedClaims)
              setClaims(mappedClaims);
            }

            // Set steps if they exist
            if (Array.isArray(workflowData.steps)) {
              const mappedSteps = workflowData.steps.map((step: any) => ({
                id: step.id || Date.now(),
                apiEndpoint: step.apiEndpoint || "",
                parameters: step.parameters || {},
                parameterMappings: Array.isArray(step.parameterMappings)
                  ? step.parameterMappings.map((m: any) => ({
                    id: m.id || Date.now(),
                    source: m.source || "",
                    target: m.target || "",
                    sourceType: m.sourceType || "claim",
                    sourceStepId: m.sourceStepId || undefined,
                  }))
                  : [],
                condition: step.condition || "",
                order: step.order || flowSteps.length + 1,
                inputJson:
                  typeof step.inputJson === "string"
                    ? step.inputJson
                    : JSON.stringify(step.inputJson || {}, null, 2),
                outputJson:
                  typeof step.outputJson === "string"
                    ? step.outputJson
                    : JSON.stringify(step.outputJson || {}, null, 2),
                requestGroup: step.requestGroup || "",
                // outputMappings: step.outputMappings || [],
                outputMappings: Array.isArray(step.outputMappings)
                  ? step.outputMappings.map((m: any) => ({
                    outputPath: m.outputPath,
                    libraryField: m.libraryField,
                  }))
                  : [],
                uploadAction: step.uploadAction
                  ? {
                    id: step.uploadAction.id || Date.now(),
                    enabled: !!step.uploadAction.enabled,
                    apiEndpoint: step.uploadAction.apiEndpoint || "",
                    parameterMappings: Array.isArray(
                      step.uploadAction.parameterMappings
                    )
                      ? step.uploadAction.parameterMappings.map((m: any) => ({
                        id: m.id || Date.now(),
                        source: m.source || "",
                        target: m.target || "",
                        sourceType: m.sourceType || "claim",
                        sourceStepId: m.sourceStepId || undefined,
                        lookupConfig: m.lookupConfig
                          ? typeof m.lookupConfig === "string"
                            ? m.lookupConfig
                            : JSON.stringify(m.lookupConfig)
                          : undefined,
                      }))
                      : [],
                    inputJson:
                      typeof step.uploadAction.inputJson === "string"
                        ? step.uploadAction.inputJson
                        : JSON.stringify(
                          step.uploadAction.inputJson || {},
                          null,
                          2
                        ),
                    outputJson:
                      typeof step.uploadAction.outputJson === "string"
                        ? step.uploadAction.outputJson
                        : JSON.stringify(
                          step.uploadAction.outputJson || {},
                          null,
                          2
                        ),
                  }
                  : undefined,
              }));
              setFlowSteps(mappedSteps);
            }
          } catch (e) {
            console.error("Error parsing flow JSON", e);
            toast({
              title: "Error",
              description: "Failed to parse existing flow data",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("Error loading execution flow", err);
        toast({
          title: "Error",
          description: "Failed to load execution flow data. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [accessToken, connectionId]);

  const addClaim = (): void => {

    const newClaim: Claim = {
      id: Date.now(),
      name: "",
      apiEndpoint: "",
      parameterSource: "claim",
      parameterKey: "",
      parameterValue: "",
      requestGroup: "",
      isLoadingRequestResponse: false,
      parameterMappings: [],
      inputJson: "",
      outputJson: "",
      isSwaggerEnabled: isSwaggerEnabled, // Default value
      swaggerUrl: swaggerUrl, // Default value
    };
    setClaims([...claims, newClaim]);
  };

  const updateClaim = (
    claimId: number,
    field: keyof Claim,
    value: any
  ): void => {
    setClaims(
      claims.map((claim) => {
        if (claim.id === claimId) {
          const updatedClaim = { ...claim, [field]: value };

          // Reset relevant fields when API changes
          if (field === "apiEndpoint") {
            updatedClaim.parameterKey = "";
            updatedClaim.parameterValue = "";
            updatedClaim.requestGroup = "";
            updatedClaim.inputJson = "";
            updatedClaim.outputJson = "";
            updatedClaim.parameterMappings = [];
          }

          // When inputJson changes, validate it and potentially clear mappings
          if (field === "inputJson") {
            try {
              JSON.parse(value); // Validate JSON
              // Optionally clear mappings if the input structure changed significantly
              // updatedClaim.parameterMappings = [];
            } catch (e) {
              console.error("Invalid JSON input", e);
              // Keep the invalid JSON but might want to handle this differently
            }
          }

          // When outputJson changes, validate it
          if (field === "outputJson") {
            try {
              JSON.parse(value); // Validate JSON
            } catch (e) {
              console.error("Invalid JSON output", e);
            }
          }

          return updatedClaim;
        }
        return claim;
      })
    );
  };

  //Flow steps data 
  /**
   * Check if a step has broken mappings (references deleted steps OR claims)
   */
  const hasInvalidMappings = (step: FlowStep, allSteps: FlowStep[], allClaims: Claim[]): boolean => {
    if (!step.parameterMappings || step.parameterMappings.length === 0) {
      return false;
    }

    // Get all valid step IDs
    const validStepIds = new Set(allSteps.map(s => s.id));

    // Check if any mapping references a deleted step or claim
    return step.parameterMappings.some(mapping => {
      // Check for deleted previous steps
      if (mapping.sourceType === 'previous_step' && mapping.sourceStepId) {
        return !validStepIds.has(mapping.sourceStepId);
      }

      // Check for deleted claims
      // A claim mapping is valid if we have at least ONE claim
      if (mapping.sourceType === 'claim') {
        // If we have NO claims at all, then claim mappings are invalid
        if (allClaims.length === 0) {
          return true;
        }

        // If the source references a specific claim output field, verify the claim exists
        // The mapping.source format is like "claim.userId" or "claim.userType"
        // We just need to verify that at least one claim has this field in its output
        if (mapping.source && mapping.source.startsWith('claim.')) {
          const fieldName = mapping.source.replace('claim.', '');

          // Check if ANY claim has this field in its outputJson
          const claimHasField = allClaims.some(claim => {
            try {
              if (!claim.outputJson) return false;
              const outputJson = JSON.parse(claim.outputJson);
              return outputJson.hasOwnProperty(fieldName);
            } catch (e) {
              console.error('Error parsing claim outputJson:', e);
              return false;
            }
          });

          // If no claim has this field, the mapping is invalid
          return !claimHasField;
        }
      }

      return false;
    });
  };

  /**
   * Count invalid mappings for a step (both steps and claims)
   */
  const getInvalidMappingsCount = (step: FlowStep, allSteps: FlowStep[], allClaims: Claim[]): number => {
    if (!step.parameterMappings || step.parameterMappings.length === 0) {
      return 0;
    }

    const validStepIds = new Set(allSteps.map(s => s.id));

    return step.parameterMappings.filter(mapping => {
      // Check for deleted previous steps
      if (mapping.sourceType === 'previous_step' && mapping.sourceStepId) {
        return !validStepIds.has(mapping.sourceStepId);
      }

      // Check for deleted claims
      if (mapping.sourceType === 'claim') {
        if (allClaims.length === 0) {
          return true;
        }

        if (mapping.source && mapping.source.startsWith('claim.')) {
          const fieldName = mapping.source.replace('claim.', '');

          const claimHasField = allClaims.some(claim => {
            try {
              if (!claim.outputJson) return false;
              const outputJson = JSON.parse(claim.outputJson);
              return outputJson.hasOwnProperty(fieldName);
            } catch (e) {
              return false;
            }
          });

          return !claimHasField;
        }
      }

      return false;
    }).length;
  };

  /**
   * Get breakdown of invalid mappings by type (steps vs claims)
   */
  const getInvalidMappingsBreakdown = (
    step: FlowStep,
    allSteps: FlowStep[],
    allClaims: Claim[]
  ): { steps: number; claims: number } => {
    if (!step.parameterMappings || step.parameterMappings.length === 0) {
      return { steps: 0, claims: 0 };
    }

    const validStepIds = new Set(allSteps.map(s => s.id));

    let brokenSteps = 0;
    let brokenClaims = 0;

    step.parameterMappings.forEach(mapping => {
      // Count deleted previous steps
      if (mapping.sourceType === 'previous_step' && mapping.sourceStepId) {
        if (!validStepIds.has(mapping.sourceStepId)) {
          brokenSteps++;
        }
      }

      // Count deleted claims
      if (mapping.sourceType === 'claim') {
        if (allClaims.length === 0) {
          brokenClaims++;
        } else if (mapping.source && mapping.source.startsWith('claim.')) {
          const fieldName = mapping.source.replace('claim.', '');

          const claimHasField = allClaims.some(claim => {
            try {
              if (!claim.outputJson) return false;
              const outputJson = JSON.parse(claim.outputJson);
              return outputJson.hasOwnProperty(fieldName);
            } catch (e) {
              return false;
            }
          });

          if (!claimHasField) {
            brokenClaims++;
          }
        }
      }
    });

    return { steps: brokenSteps, claims: brokenClaims };
  };


  // Add this temporary debug function
  const debugStepMappings = (step: FlowStep) => {
    console.log('=== DEBUG STEP ===', step.apiEndpoint);
    console.log('Total mappings:', step.parameterMappings.length);

    step.parameterMappings.forEach((mapping, idx) => {
      console.log(`Mapping ${idx + 1}:`, {
        source: mapping.source,
        target: mapping.target,
        sourceType: mapping.sourceType,
        sourceStepId: mapping.sourceStepId
      });

      if (mapping.sourceType === 'claim') {
        const fieldName = mapping.source.replace('claim.', '');
        console.log(`  Looking for claim field: ${fieldName}`);

        claims.forEach(claim => {
          console.log(`  Claim "${claim.name}" outputJson:`, claim.outputJson);
          try {
            const output = JSON.parse(claim.outputJson || '{}');
            console.log(`  Parsed output:`, output);
            console.log(`  Has field "${fieldName}"?`, output.hasOwnProperty(fieldName));
          } catch (e) {
            console.log(`  Error parsing:`, e);
          }
        });
      }
    });
  };


  // Reorder steps to ensure no conflicts
  const reorderSteps = (steps: FlowStep[]): FlowStep[] => {
    return steps
      .sort((a, b) => a.order - b.order)
      .map((step, index) => ({ ...step, order: index + 1 }));
  };

  // Add step at the beginning (top)
  const addStepAtTop = () => {
    const newSteps = flowSteps.map(step => ({
      ...step,
      order: step.order + 1
    }));

    const newStep: FlowStep = {
      id: Date.now(),
      apiEndpoint: "",
      parameters: {},
      parameterMappings: [],
      condition: "",
      order: 1,
      inputJson: "",
      outputJson: "",
      requestGroup: "",
      outputMappings: [],
      isLoadingRequestResponse: false,
      isSwaggerEnabled: false,
      swaggerUrl: "",
    };

    const allSteps = [newStep, ...newSteps];
    setFlowSteps(reorderSteps(allSteps));
  };


  // Add step at the end (bottom) - Your existing addStep logic
  const addStepAtBottom = () => {
    const newStep: FlowStep = {
      id: Date.now(),
      apiEndpoint: "",
      parameters: {},
      parameterMappings: [],
      condition: "",
      order: flowSteps.length + 1,
      inputJson: "",
      outputJson: "",
      requestGroup: "",
      outputMappings: [],
      isLoadingRequestResponse: false,
      isSwaggerEnabled: false,
      swaggerUrl: "",
    };
    setFlowSteps([...flowSteps, newStep]);
  };

  // Add step between two steps
  const addStepBetween = (afterIndex: number) => {
    const newSteps = [...flowSteps];
    const insertOrder = newSteps[afterIndex].order + 1;

    // Increment order for all steps after insertion point
    for (let i = afterIndex + 1; i < newSteps.length; i++) {
      newSteps[i] = { ...newSteps[i], order: newSteps[i].order + 1 };
    }

    const newStep: FlowStep = {
      id: Date.now(),
      apiEndpoint: "",
      parameters: {},
      parameterMappings: [],
      condition: "",
      order: insertOrder,
      inputJson: "",
      outputJson: "",
      requestGroup: "",
      outputMappings: [],
      isLoadingRequestResponse: false,
      isSwaggerEnabled: false,
      swaggerUrl: "",
    };

    newSteps.splice(afterIndex + 1, 0, newStep);
    setFlowSteps(reorderSteps(newSteps));
  };

  const removeStep = (stepId: number): void => {
    setFlowSteps(flowSteps.filter((step) => step.id !== stepId));
  };

  const updateStep = (
    stepId: number,
    field: keyof FlowStep,
    value: any
  ): void => {
    setFlowSteps(
      flowSteps.map((step) => {
        if (step.id === stepId) {
          const updatedStep = { ...step, [field]: value };

          // Reset parameters and JSON data when API changes
          if (field === "apiEndpoint") {
            updatedStep.parameters = {};
            updatedStep.inputJson = "";
            updatedStep.outputJson = "";
            updatedStep.requestGroup = "";
          }

          return updatedStep;
        }
        return step;
      })
    );
  };

  const updateParameter = (
    stepId: number,
    paramName: string,
    paramValue: string
  ): void => {
    setFlowSteps(
      flowSteps.map((step) => {
        if (step.id === stepId) {
          return {
            ...step,
            parameters: {
              ...step.parameters,
              [paramName]: paramValue,
            },
          };
        }
        return step;
      })
    );
  };

  const updateParameterMapping = (
    stepId: number,
    mapping: ParameterMapping
  ): void => {
    setFlowSteps(
      flowSteps.map((step) => {
        if (step.id === stepId) {
          const updatedMappings = step.parameterMappings.map((m) =>
            m.id === mapping.id ? mapping : m
          );
          return { ...step, parameterMappings: updatedMappings };
        }
        return step;
      })
    );
  };

  const addParameterMapping = (stepId: number): void => {
    setFlowSteps(
      flowSteps.map((step) => {
        if (step.id === stepId) {
          const newMapping: ParameterMapping = {
            id: Date.now(),
            source: "",
            target: "",
            sourceType: "claim",
          };
          return {
            ...step,
            parameterMappings: [...step.parameterMappings, newMapping],
          };
        }
        return step;
      })
    );
  };

  const removeParameterMapping = (stepId: number, mappingId: number): void => {
    setFlowSteps(
      flowSteps.map((step) => {
        if (step.id === stepId) {
          return {
            ...step,
            parameterMappings: step.parameterMappings.filter(
              (m) => m.id !== mappingId
            ),
          };
        }
        return step;
      })
    );
  };

  const handleRequestTypeSelect = async (
    id: number,
    item: RequestType,
    requestGroup: string,
    isClaim: boolean = false,
    isSwaggerEnabled: boolean = false,
    swaggerUrl: string
  ): Promise<void> => {

    if (!accessToken || !connectionId) {
      toast({
        title: "Error",
        description: "Missing connection information",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const target = isClaim
      ? claims.find((claim) => claim.id === id)
      : flowSteps.find((step) => step.id === id);

    if (!target) return;

    // const { isSwaggerEnabled, swaggerUrl } = target;

    if (isSwaggerEnabled && swaggerUrl) {
      try {
        // Set loading state
        if (isClaim) {
          setClaims(
            claims.map((claim) =>
              claim.id === id
                ? { ...claim, isLoadingRequestResponse: true }
                : claim
            )
          );
        } else {
          setFlowSteps(
            flowSteps.map((step) =>
              step.id === id
                ? { ...step, isLoadingRequestResponse: true }
                : step
            )
          );
        }

        try {
          const payload: RequestResponseDTO = {
            connectionId: parseInt(connectionId),
            requestType: item.name,
            requestGroup: requestGroup,
          };

          const response = await fetchRequestResponse(accessToken, payload);
          console.log("Responce--- : ", response);
          if (response) {
            if (isClaim) {
              setClaims(
                claims.map((claim) =>
                  claim.id === id
                    ? {
                      ...claim,
                      apiEndpoint: item.name,
                      requestGroup: requestGroup,
                      inputJson: response.inputJson || "",
                      outputJson: response.outputJson || "",
                      parameterValue: response.outputJson || "",
                      isLoadingRequestResponse: false,
                    }
                    : claim
                )
              );
            } else {
              setFlowSteps(
                flowSteps.map((step) =>
                  step.id === id
                    ? {
                      ...step,
                      apiEndpoint: item.name,
                      requestGroup: requestGroup,
                      inputJson: response.inputJson || "",
                      outputJson: response.outputJson || "",
                      isLoadingRequestResponse: false,
                    }
                    : step
                )
              );
            }
          }
        } catch (error) {
          console.error("Error fetching request/response data:", error);

          // Clear loading state on error
          if (isClaim) {
            setClaims(
              claims.map((claim) =>
                claim.id === id
                  ? { ...claim, isLoadingRequestResponse: false }
                  : claim
              )
            );
          } else {
            setFlowSteps(
              flowSteps.map((step) =>
                step.id === id
                  ? { ...step, isLoadingRequestResponse: false }
                  : step
              )
            );
          }

          toast({
            title: "Error",
            description: "Failed to fetch request/response data",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching Swagger endpoints:", error);
        toast({
          title: "Error",
          description: "Failed to fetch Swagger endpoints.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      // Handle manual input case
      toast({
        title: "Manual Input",
        description: "Please manually enter the request and response.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  const generateFlowJson = (): string => {
    const flowJson = {
      name: flowName,
      description: flowDescription,
      version: "1.0",
      createdAt: new Date().toISOString(),
      connectionId: connectionId ? parseInt(connectionId) : 0,
      claims: claims.map((claim) => ({
        id: claim.id, // Keep existing ID
        name: claim.name,
        apiEndpoint: claim.apiEndpoint,
        parameterSource: claim.parameterSource,
        parameterKey: claim.parameterKey,
        parameterValue: isSwaggerEnabled
          ? claim.parameterValue
          : claim.outputJson || "",
        requestGroup: claim.requestGroup || null,
        parameterMappings: claim.parameterMappings.map((mapping) => ({
          id: mapping.id, // Keep existing ID
          source: mapping.source,
          target: mapping.target,
          sourceType: mapping.sourceType,
          sourceStepId: mapping.sourceStepId || null,
        })),
        inputJson: claim.inputJson || null,
        outputJson: claim.outputJson || null,
      })),
      steps: flowSteps.map((step) => ({
        id: step.id, // Keep existing ID
        order: step.order,
        apiEndpoint: step.apiEndpoint,
        parameters: step.parameters,
        parameterMappings: step.parameterMappings.map((mapping) => ({
          id: mapping.id, // Keep existing ID
          source: mapping.source,
          target: mapping.target,
          sourceType: mapping.sourceType,
          sourceStepId: mapping.sourceStepId || null,
        })),
        condition: step.condition || null,
        inputJson: step.inputJson || null,
        outputJson: step.outputJson || null,
        requestGroup: step.requestGroup || null,
        outputMappings:
          step.outputMappings?.map((mapping) => ({
            outputPath: mapping.outputPath,
            libraryField: mapping.libraryField,
          })) || [],
        uploadAction: step.uploadAction
          ? {
            apiEndpoint: step.uploadAction.apiEndpoint,
            inputJson: convertUploadInputJsonForStorage(
              step.uploadAction.inputJson
            ),
            outputJson: step.uploadAction.outputJson || null,
            parameterMappings: step.uploadAction.parameterMappings.map(
              (mapping) => ({
                id: mapping.id,
                source: mapping.source,
                target: mapping.target,
                sourceType: mapping.sourceType,
                sourceStepId: mapping.sourceStepId || null,
                ...(mapping.lookupConfig && {
                  lookupConfig: {
                    endpoint: JSON.parse(mapping.lookupConfig).endpoint,
                    inputJson: JSON.parse(mapping.lookupConfig).inputJson,
                    outputJson: JSON.parse(mapping.lookupConfig).outputJson,
                    mappings: JSON.parse(mapping.lookupConfig).mappings.map(
                      (m: any) => ({
                        outputPath: m.outputPath,
                        valuePath: m.valuePath,
                      })
                    ),
                  },
                }),
              })
            ),
          }
          : null,
      })),
    };

    console.log(":flowJson", flowJson.steps)
    return JSON.stringify(flowJson, null, 2);
  };
  const convertUploadInputJsonForStorage = (inputJsonStr: string): string => {
    if (!inputJsonStr) return inputJsonStr;

    try {
      const parsed = JSON.parse(inputJsonStr);

      // Check if it's an object with a single property that's an array
      if (
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        parsed !== null
      ) {
        const keys = Object.keys(parsed);

        if (keys.length === 1) {
          const firstKey = keys[0];
          const firstValue = parsed[firstKey];

          if (Array.isArray(firstValue)) {
            // Return the array directly as a JSON string
            return JSON.stringify(firstValue, null, 2);
          }
        }
      }

      // If no conversion needed, return original
      return inputJsonStr;
    } catch (error) {
      console.error("Error parsing upload input JSON for storage:", error);
      return inputJsonStr;
    }
  };

  const saveFlow = async (): Promise<void> => {
    if (flowSteps.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one step to the flow",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const executionFlowData: ExecutionFlowModel = {
      // You must define this
      executionType: "Manual",
      name: "Demo",
      connectionId: connectionId ? parseInt(connectionId, 10) : 0,
      updatedBy: userData?.userID ?? 0, // fallback if undefined
      updatedAt: new Date().toISOString(),
      workflowJson: generateFlowJson(),
    };

    console.log(
      "Updating execution flow:",
      JSON.stringify(executionFlowData, null, 2)
    );

    try {
      const result = await updateExecutionFlow(accessToken, executionFlowData);
      console.log("Saving flow:", executionFlowData.workflowJson);

      toast({
        title: "Success",
        description: "UI Flow saved successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving flow:", error);
      toast({
        title: "Error",
        description: "Failed to save execution flow.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box p={6} bg="gray.50" minH="100vh">
        <Text textAlign="center" fontSize="lg" color="gray.600">
          Loading API endpoints...
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box p={6} bg="gray.50" minH="100vh">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          {/* Header */}
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* Connection Info Section */}
                <Flex justify="space-between" align="start">
                  <VStack align="start" spacing={3} flex={1}>
                    <HStack spacing={3}>
                      <Box
                        p={2}
                        bg="blue.50"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Workflow size={24} color="#3182CE" />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                          {connection.name || 'Untitled Connection'}
                        </Text>
                        <HStack spacing={2} divider={<Text color="gray.300">•</Text>}>
                          <Badge colorScheme="blue" fontSize="xs">
                            {connection.connectionType || 'API'}
                          </Badge>
                          <Badge colorScheme="purple" fontSize="xs" variant="outline">
                            {connection.actionType}
                          </Badge>
                        </HStack>
                        {/* Add Description */}
                        {connection?.description && (
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            {connection.description}
                          </Text>
                        )}
                      </VStack>
                    </HStack>

                    {/* Connection Details */}
                    <Box
                      w="full"
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderLeft="3px solid"
                      borderColor="blue.400"
                    >
                      <VStack align="start" spacing={2}>
                        {connection.apiUrl && (
                          <HStack spacing={2} align="center">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600" minW="80px">
                              API URL:
                            </Text>
                            <Text fontSize="xs" color="gray.700" fontFamily="mono" flex={1}>
                              {connection.apiUrl}
                            </Text>
                            <Tooltip label="Open API URL">
                              <IconButton
                                aria-label="Open API URL"
                                icon={<ExternalLink size={14} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => window.open(connection.apiUrl, '_blank')}
                              />
                            </Tooltip>
                          </HStack>
                        )}
                        {connection.isSwaggerEnabled && connection.swaggerUrl && (
                          <HStack spacing={2} align="center">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600" minW="80px">
                              Swagger:
                            </Text>
                            <Text fontSize="xs" color="blue.600" fontFamily="mono" flex={1}>
                              {connection.swaggerUrl}
                            </Text>
                            <HStack spacing={1}>
                              <Badge colorScheme="green" fontSize="xs">
                                Enabled
                              </Badge>
                              <Tooltip label="Open Swagger Documentation">
                                <IconButton
                                  aria-label="Open Swagger"
                                  icon={<ExternalLink size={14} />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => window.open(connection.swaggerUrl, '_blank')}
                                />
                              </Tooltip>
                            </HStack>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  </VStack>

                  {/* Action Buttons */}
                  <HStack spacing={3}>
                    <Button
                      leftIcon={<Code size={16} />}
                      variant="outline"
                      onClick={() => setShowJsonPreview(!showJsonPreview)}
                      size="sm"
                    >
                      {showJsonPreview ? "Hide" : "Show"} JSON
                    </Button>
                    <Button
                      leftIcon={<Save size={16} />}
                      colorScheme="blue"
                      onClick={saveFlow}
                      size="sm"
                    >
                      Save Flow
                    </Button>
                  </HStack>
                </Flex>

                {/* Divider */}
                <Divider />

                {/* Workflow Section */}
                <Flex justify="space-between" align="center">
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      bg="purple.50"
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="xl">🔧</Text>
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xl" fontWeight="bold" color="gray.800">
                        UI Flow
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Configure claims and steps for your flow
                      </Text>
                    </VStack>
                  </HStack>
                </Flex>
              </VStack>
            </CardBody>
          </Card>

          {/* JSON Preview */}
          {showJsonPreview && (
            <Card>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  <Flex justify="space-between" align="center">
                    <HStack>
                      <Code size={16} />
                      <Text fontWeight="600" color="gray.700">
                        Flow Configuration JSON
                      </Text>
                    </HStack>
                    <Button
                      size="sm"
                      leftIcon={<Copy size={14} />}
                      colorScheme="green"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(generateFlowJson());
                        // Optional: Add a toast notification
                        toast({
                          title: "Copied to clipboard",
                          status: "success",
                          duration: 2000,
                          isClosable: true,
                        });
                      }}
                    >
                      Copy JSON
                    </Button>
                  </Flex>
                  <Box
                    as="pre"
                    bg="gray.900"
                    color="green.300"
                    p={4}
                    borderRadius="md"
                    fontSize="sm"
                    overflow="auto"
                    maxH="300px"
                    w="full"
                    maxW="100%"
                    whiteSpace="pre-wrap"
                    wordBreak="break-all"
                  >
                    {generateFlowJson()}
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Claims Section */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center">
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    🔑 Claims Configuration
                  </Text>
                  <Button
                    leftIcon={<Plus size={16} />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={addClaim}
                    size="sm"
                  >
                    Add Claim
                  </Button>
                </Flex>
                <Text fontSize="sm" color="gray.500">
                  Configure claims that will be used to fetch required data
                  before executing the flow steps.
                </Text>

                {claims.length === 0 ? (
                  <Card variant="outline" borderStyle="dashed" mt={4}>
                    <CardBody textAlign="center" py={8}>
                      <VStack spacing={3}>
                        <Box fontSize="3xl">🔑</Box>
                        <Text fontSize="md" color="gray.600">
                          No claims configured yet
                        </Text>
                        <Text color="gray.500" fontSize="sm">
                          Add claims to fetch required data before flow
                          execution
                        </Text>
                      </VStack>
                    </CardBody>
                  </Card>
                ) : (
                  // <VStack spacing={4} align="stretch" mt={4}>
                  //   {claims.map((claim, index) => (
                  //     <ClaimCard
                  //       key={claim.id}
                  //       claim={claim}
                  //       claimNumber={index + 1}
                  //       swaggerEndpoints={swaggerEndpoints}
                  //       onUpdateClaim={updateClaim}
                  //       onRemoveClaim={removeClaim}
                  //       onRequestTypeSelect={(claimId, item, requestGroup) =>
                  //         handleRequestTypeSelect(
                  //           claimId,
                  //           item,
                  //           requestGroup,
                  //           true
                  //         )
                  //       }
                  //     />
                  //   ))}
                  // </VStack>

                  <VStack spacing={2} align="stretch" mt={4}>
                    {claims.map((claim, index) => (
                      <Card
                        key={claim.id}
                        variant="outline"
                        cursor="pointer"
                        onClick={() => handleClaimClick(claim)}
                        _hover={{ shadow: "md", borderColor: "blue.300" }}
                        transition="all 0.2s"
                      >
                        <CardBody py={3}>
                          <Flex justify="space-between" align="center">
                            <HStack spacing={3}>
                              <Badge colorScheme="blue" variant="solid">
                                CLAIM {index + 1}
                              </Badge>
                              <Text fontWeight="medium" color="gray.700">
                                {claim.name || "Untitled Claim"}
                              </Text>
                              {claim.apiEndpoint && (
                                <Badge colorScheme="green" variant="outline">
                                  {claim.apiEndpoint}
                                </Badge>
                              )}
                            </HStack>
                            <HStack spacing={2}>
                              <IconButton
                                aria-label="Delete claim"
                                icon={<Trash2 size={14} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                onClick={(e) =>
                                  handleClaimDeleteClick(claim.id, e)
                                }
                              />
                              <ChevronRight size={16} color="gray.400" />
                            </HStack>
                          </Flex>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Flow Steps */}
          {/* Flow Steps - Enhanced with Visual Flow */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                {/* Header */}
                <Flex justify="space-between" align="center">
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    ⚡ Flow Steps
                  </Text>
                  <Button
                    leftIcon={<Plus size={16} />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={addStepAtBottom}
                    size="sm"
                  >
                    Add Step
                  </Button>
                </Flex>

                {/* Empty State */}
                {flowSteps.length === 0 ? (
                  <Card variant="outline" borderStyle="dashed" mt={4}>
                    <CardBody textAlign="center" py={8}>
                      <VStack spacing={3}>
                        <Box fontSize="3xl">⚡</Box>
                        <Text fontSize="md" color="gray.600">
                          No steps in your flow yet
                        </Text>
                        <Text color="gray.500" fontSize="sm">
                          Add your first step to start building your flow
                        </Text>
                        <Button
                          leftIcon={<Plus size={16} />}
                          colorScheme="blue"
                          size="sm"
                          onClick={addStepAtTop}
                        >
                          Create First Step
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                ) : (
                  <Box mt={4} position="relative">
                    {/* Add button at top */}
                    <Flex justify="center" direction="column" align="center" py={3}>
                      <Tooltip label="Add step at top" placement="top">
                        <IconButton
                          aria-label="Add step at start"
                          icon={<Plus size={14} />}
                          size="xs"
                          colorScheme="blue"
                          isRound
                          onClick={addStepAtTop}
                          _hover={{ transform: 'scale(1.1)', bg: 'blue.600' }}
                          transition="all 0.2s"
                        />
                      </Tooltip>
                      <Box w="3px" h="20px" bg="blue.300" borderRadius="full" />
                    </Flex>

                    {/* Workflow Flow Container */}
                    <VStack spacing={0} align="stretch">
                      {flowSteps.map((step, index) => (
                        <Box key={step.id} position="relative">
                          {/* Step Card with Enhanced Visual Design */}
                          <Box position="relative" zIndex={2}>
                            <Card
                              variant="outline"
                              cursor="pointer"
                              onClick={() => handleStepClick(step)}
                              _hover={{
                                shadow: "lg",
                                borderColor: hasInvalidMappings(step, flowSteps, claims)
                                  ? "red.500"
                                  : step.parameterMappings.length > 0
                                    ? "green.400"
                                    : "blue.400",
                                transform: "translateY(-2px)",
                              }}
                              transition="all 0.3s ease"
                              bg="white"
                              borderWidth="2px"
                              borderColor={
                                hasInvalidMappings(step, flowSteps, claims)
                                  ? "red.400"
                                  : step.parameterMappings.length > 0
                                    ? "green.200"
                                    : "gray.200"
                              }
                            >
                              <CardBody py={4} px={6}>
                                <Flex justify="space-between" align="center">
                                  <HStack spacing={4}>
                                    {/* Step Number Circle */}
                                    <Box
                                      w="40px"
                                      h="40px"
                                      borderRadius="full"
                                      bg={hasInvalidMappings(step, flowSteps, claims) ? "red.500" : "blue.500"}
                                      color="white"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      fontWeight="bold"
                                      fontSize="sm"
                                      boxShadow="md"
                                    >
                                      {index + 1}
                                    </Box>

                                    <VStack align="start" spacing={1}>
                                      <HStack spacing={2}>
                                        <Text fontWeight="bold" color="gray.800" fontSize="md">
                                          {step.apiEndpoint || "Configure API Endpoint"}
                                        </Text>
                                        {step.condition && (
                                          <Badge colorScheme="orange" variant="solid" fontSize="xs">
                                            Conditional
                                          </Badge>
                                        )}
                                        {hasInvalidMappings(step, flowSteps, claims) && (
                                          <Tooltip
                                            label={(() => {
                                              const breakdown = getInvalidMappingsBreakdown(step, flowSteps, claims);
                                              const parts = [];
                                              if (breakdown.steps > 0) parts.push(`${breakdown.steps} deleted step(s)`);
                                              if (breakdown.claims > 0) parts.push(`${breakdown.claims} deleted claim(s)`);
                                              return `Mappings reference: ${parts.join(', ')}`;
                                            })()}
                                          >
                                            <Badge colorScheme="red" variant="solid" fontSize="xs">
                                              ⚠ Invalid Mappings
                                            </Badge>
                                          </Tooltip>
                                        )}
                                      </HStack>

                                      {/* Step Details */}
                                      <HStack spacing={3}>
                                        {step.requestGroup && (
                                          <Badge colorScheme="purple" variant="outline" fontSize="xs">
                                            {step.requestGroup}
                                          </Badge>
                                        )}
                                        {step.parameterMappings.length > 0 && (
                                          <Badge
                                            colorScheme={hasInvalidMappings(step, flowSteps, claims) ? "red" : "green"}
                                            variant="outline"
                                            fontSize="xs"
                                          >
                                            {step.parameterMappings.length} Mapping{step.parameterMappings.length !== 1 ? "s" : ""}
                                            {hasInvalidMappings(step, flowSteps, claims) && ` (${getInvalidMappingsCount(step, flowSteps, claims)} broken)`}
                                          </Badge>
                                        )}
                                        {Object.keys(step.parameters).length > 0 && (
                                          <Badge colorScheme="cyan" variant="outline" fontSize="xs">
                                            {Object.keys(step.parameters).length} Parameter{Object.keys(step.parameters).length !== 1 ? "s" : ""}
                                          </Badge>
                                        )}
                                      </HStack>
                                    </VStack>
                                  </HStack>

                                  <HStack spacing={2}>
                                    {step.isLoadingRequestResponse && (
                                      <Spinner size="sm" color="blue.500" />
                                    )}
                                    <Tooltip label="Configure Step">
                                      <IconButton
                                        aria-label="Configure step"
                                        icon={<Settings size={16} />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="blue"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStepClick(step);
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip label="Delete Step">
                                      <IconButton
                                        aria-label="Delete step"
                                        icon={<Trash2 size={16} />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={(e) => handleStepDeleteClick(step.id, e)}
                                      />
                                    </Tooltip>
                                    <ChevronRight size={18} color="#A0AEC0" />
                                  </HStack>
                                </Flex>
                              </CardBody>
                            </Card>
                          </Box>

                          {/* Flow Arrow - Only show between steps, not after the last one */}
                          {index < flowSteps.length - 1 && (
                            <Box position="relative" zIndex={1}>
                              <Flex justify="center" py={3}>
                                <VStack spacing={0}>
                                  {/* Vertical Line */}
                                  <Box w="3px" h="20px" bg="blue.300" borderRadius="full" />
                                  {/* Add Step Button in the middle of the flow */}
                                  {/* Add Step Button */}
                                  <Tooltip label="Add step here" placement="right">
                                    <IconButton
                                      aria-label="Add step between"
                                      icon={<Plus size={14} />}
                                      size="xs"
                                      colorScheme="blue"
                                      isRound
                                      position="absolute"
                                      top="50%"
                                      left="50%"
                                      transform="translate(-50%, -50%)"
                                      zIndex={3}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addStepBetween(index);
                                      }}
                                      _hover={{ transform: 'translate(-50%, -50%) scale(1.1)', bg: 'blue.600' }}
                                      transition="all 0.2s"
                                    />
                                  </Tooltip>
                                  {/* Arrow Icon */}
                                  <Box position="relative">
                                    <ChevronDown size={20} color="#90CDF4" strokeWidth={3} />
                                  </Box>
                                  {/* Flow Label */}
                                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mt={1}>
                                    THEN
                                  </Text>
                                </VStack>
                              </Flex>
                            </Box>
                          )}


                          {/* Success/Complete indicator for last step */}
                          {index === flowSteps.length - 1 && (


                            <Box position="relative" zIndex={1}>
                              {/* Add button at bottom */}
                              <Flex justify="center" direction="column" align="center" py={3}>
                                <Box w="3px" h="20px" bg="blue.300" borderRadius="full" />
                                <Tooltip label="Add step at bottom" placement="bottom">
                                  <IconButton
                                    aria-label="Add step at end"
                                    icon={<Plus size={14} />}
                                    size="xs"
                                    colorScheme="blue"
                                    isRound
                                    onClick={addStepAtBottom}
                                    _hover={{ transform: 'scale(1.1)', bg: 'blue.600' }}
                                    transition="all 0.2s"
                                  />
                                </Tooltip>
                              </Flex>
                              <Flex justify="center" py={4}>
                                <VStack spacing={2}>
                                  <Box w="3px" h="20px" bg="blue.300" borderRadius="full" />
                                  <Box
                                    w="32px"
                                    h="32px"
                                    borderRadius="full"
                                    bg="green.500"
                                    color="white"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    boxShadow="md"
                                  >
                                    <Box fontSize="16px">✓</Box>
                                  </Box>
                                  <Text fontSize="xs" color="green.600" fontWeight="bold">
                                    COMPLETE
                                  </Text>
                                </VStack>
                              </Flex>


                            </Box>
                          )}
                        </Box>
                      ))}
                    </VStack>

                    {/* Optional: Add a subtle background pattern for the flow */}
                    {flowSteps.length > 1 && (
                      <Box
                        position="absolute"
                        left="50%"
                        top="40px"
                        bottom="80px"
                        w="1px"
                        bg="gray.100"
                        zIndex={0}
                        transform="translateX(-50%)"
                      />
                    )}
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      {/* Claim Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={claimDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setClaimDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Claim
            </AlertDialogHeader>

            <AlertDialogBody>
              {claimDeleteConfirmationData && (
                <VStack align="stretch" spacing={4}>
                  <Text>
                    Are you sure you want to delete the claim{" "}
                    <strong>"{claimDeleteConfirmationData.claimName}"</strong>?
                  </Text>

                  {claimDeleteConfirmationData.affectedSteps.length > 0 && (
                    <Box>
                      <Text color="orange.600" fontWeight="semibold" mb={2}>
                        ⚠️ This claim is referenced by the following flow
                        steps:
                      </Text>
                      <VStack align="stretch" spacing={2} ml={4}>
                        {claimDeleteConfirmationData.affectedSteps.map(
                          (step) => (
                            <Box
                              key={step.id}
                              p={2}
                              bg="orange.50"
                              borderRadius="md"
                              borderLeft="3px solid"
                              borderColor="orange.400"
                            >
                              <Text fontSize="sm">
                                <strong>Step {step.index}:</strong> {step.name}
                              </Text>
                            </Box>
                          )
                        )}
                      </VStack>
                      <Text color="orange.600" fontSize="sm" mt={2}>
                        Parameter mappings referencing this claim will be
                        removed from these steps.
                      </Text>
                    </Box>
                  )}

                  <Text color="gray.600" fontSize="sm">
                    This action cannot be undone.
                  </Text>
                </VStack>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setClaimDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmClaimDeletion} ml={3}>
                Delete Claim
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Workflow Step
            </AlertDialogHeader>

            <AlertDialogBody>
              {deleteConfirmationData && (
                <VStack align="stretch" spacing={4}>
                  <Text>
                    Are you sure you want to delete{" "}
                    <strong>"{deleteConfirmationData.stepName}"</strong>?
                  </Text>

                  {deleteConfirmationData.dependentSteps.length > 0 && (
                    <Box>
                      <Text color="red.600" fontWeight="semibold" mb={2}>
                        ⚠️ This action will also delete the following dependent
                        steps:
                      </Text>
                      <VStack align="stretch" spacing={2} ml={4}>
                        {deleteConfirmationData.dependentSteps.map(
                          (depStep) => (
                            <Box
                              key={depStep.id}
                              p={2}
                              bg="red.50"
                              borderRadius="md"
                              borderLeft="3px solid"
                              borderColor="red.400"
                            >
                              <Text fontSize="sm">
                                <strong>Step {depStep.index + 1}:</strong>{" "}
                                {depStep.name}
                              </Text>
                            </Box>
                          )
                        )}
                      </VStack>
                      <Text color="red.600" fontSize="sm" mt={2}>
                        Total steps to be deleted:{" "}
                        <strong>
                          {deleteConfirmationData.dependentSteps.length + 1}
                        </strong>
                      </Text>
                    </Box>
                  )}

                  <Text color="gray.600" fontSize="sm">
                    This action cannot be undone.
                  </Text>
                </VStack>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmStepDeletion} ml={3}>
                Delete{" "}
                {deleteConfirmationData?.dependentSteps.length
                  ? `${deleteConfirmationData.dependentSteps.length + 1} Steps`
                  : "Step"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Claim Drawer */}
      <Drawer
        isOpen={claimDrawerOpen}
        placement="right"
        onClose={handleCloseClaimDrawer}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <VStack align="start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                Configure Claim
              </Text>
              <Text fontSize="sm" color="gray.500">
                Set up API endpoint and parameter mappings
              </Text>
            </VStack>
          </DrawerHeader>

          <DrawerBody>
            {selectedClaim && (
              <ClaimCard
                claim={selectedClaim}
                claimNumber={
                  claims.findIndex((c) => c.id === selectedClaim.id) + 1
                }
                swaggerEndpoints={swaggerEndpoints}
                isSwaggerEnabled={isSwaggerEnabled} // Pass `isSwaggerEnabled`
                swaggerUrl={swaggerUrl} // Pass `swaggerUrl`
                onUpdateClaim={updateSelectedClaim}
                onRemoveClaim={handleClaimDeleteClick}
                onRequestTypeSelect={async (claimId, item, requestGroup, isSwaggerEnabled, swaggerUrl) => {
                  await handleRequestTypeSelect(
                    claimId,
                    item,
                    requestGroup,
                    true,
                    isSwaggerEnabled,
                    swaggerUrl
                  );
                  const updatedClaim = claims.find((c) => c.id === claimId);
                  if (updatedClaim) {
                    setSelectedClaim(updatedClaim);
                  }
                }}
              />
            )}
          </DrawerBody>

          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={handleCloseClaimDrawer}>
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Step Drawer */}
      <Drawer
        isOpen={stepDrawerOpen}
        placement="right"
        onClose={handleCloseStepDrawer}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <VStack align="start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                Configure Step
              </Text>
              <Text fontSize="sm" color="gray.500">
                Set up API endpoint, parameters, and mappings
              </Text>
            </VStack>
          </DrawerHeader>

          <DrawerBody>
            {selectedStep && (
              <StepCard
                step={selectedStep}
                stepNumber={
                  flowSteps.findIndex((s) => s.id === selectedStep.id) + 1
                }
                swaggerEndpoints={swaggerEndpoints}
                isSwaggerEnabled={isSwaggerEnabled} // Pass `isSwaggerEnabled`
                swaggerUrl={swaggerUrl} // Pass `swaggerUrl`
                onUpdateStep={updateSelectedStep}
                onUpdateParameter={updateSelectedStepParameter}
                onUpdateParameterMapping={updateSelectedStepParameterMapping}
                onRemoveParameterMapping={removeSelectedStepParameterMapping}
                onAddParameterMapping={addSelectedStepParameterMapping}
                onRemoveStep={(stepId) => handleStepDeleteClick(stepId)}
                onRequestTypeSelect={async (stepId, item, requestGroup, isSwaggerEnabled, swaggerUrl) => {

                  await handleRequestTypeSelect(
                    stepId,
                    item,
                    requestGroup,
                    false,
                    isSwaggerEnabled,
                    swaggerUrl
                  );
                  const updatedStep = flowSteps.find((s) => s.id === stepId);
                  if (updatedStep) {
                    setSelectedStep(updatedStep);
                  }
                }}
                claims={claims}
                previousSteps={flowSteps.slice(
                  0,
                  flowSteps.findIndex((s) => s.id === selectedStep.id)
                )}
                accessToken={accessToken}
                conId={parseInt(connectionId!)}
              />
            )}
          </DrawerBody>

          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={handleCloseStepDrawer}>
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ExecutionFlowBuilder;
